import Vue from "vue";
import Router from "vue-router";
import store from "./store.js";
import AboutWork from "./views/about/Work.vue";
import AboutLogo from "./views/about/Logo.vue";
import AboutTerms from "./views/about/Terms.vue";
import AboutContact from "./views/about/Contact.vue";
import EmptyHome from "./views/EmptyHome.vue";
import UserHome from "./views/UserHome.vue";
import Login from "./views/Login.vue";
import Register from "./views/Register.vue";
import RegisterSuccess from "./views/RegisterSuccess.vue";
import ForgotPassword from "./views/ForgotPassword.vue";
import PasswordUpdate from "./views/PasswordUpdate.vue";
import ManageUsers from "./views/admin/ManageUsers.vue";
import ManageRoles from "./views/admin/ManageRoles.vue";
import PageNotFound from "./views/PageNotFound.vue";
import EventPhoto from "./views/content/EventPhoto.vue";
import EventMessageList from "./views/content/EventMessageList.vue";
import CategoryPhoto from "./views/content/CategoryPhoto.vue";
import TempVideoList from "./views/content/TempVideoList.vue";
import LiveStream from "./views/content/LiveStream.vue";

Vue.use(Router);

let router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "root",
      redirect: "/home",
    },
    {
      path: "/about/work",
      name: "about-work",
      component: AboutWork,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/about/logo",
      name: "about-logo",
      component: AboutLogo,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/about/terms",
      name: "about-terms",
      component: AboutTerms,
      meta: {
        requiresAuth: true,
        requiresActivation: true
      }
    },
    {
      path: "/about/contact",
      name: "about-contact",
      component: AboutContact,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/authorvideolist/:id",
      name: "author-video-list",
      component: TempVideoList,
      props: true,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/event/photo/:id",
      name: "event-photo",
      component: EventPhoto,
      props: true,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/category/messagelist/:id",
      name: "category-messagelist",
      component: EventMessageList,
      props: true,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/category/messagelist/:id/:customBehaviour",
      name: "category-messagelist-custom-behaviour",
      component: EventMessageList,
      props: true,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/livestream/",
      name: "livestream",
      component: LiveStream,
      props: true,
      meta: {
        requiresAuth: true,
        requiresActivation: true
      }
    },
    {
      path: "/category/eventphotolist/:id",
      name: "category-eventphotolist",
      component: CategoryPhoto,
      props: true,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      path: "/event/photolist/:id",
      name: "eventphotolist",
      component: EventPhoto,
      props: true,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresMember: true
      }
    },
    {
      name: "emptyhome",
      path: "/emptyhome",
      component: EmptyHome,
      // non-activated users directed emptyhome
      meta: {
        requiresAuth: true
      }
    },
    {
      path: "/home",
      name: "home",
      component: UserHome,
      // activated users directed here
      meta: {
        requiresAuth: true,
        requiresActivation: true
      }
    },
    {
      path: "/login",
      name: "login",
      component: Login
      // this path is open to the public to login
    },
    {
      path: "/register",
      name: "register",
      component: Register
      // this path is open to the public to register
    },
    {
      path: "/registersuccess",
      name: "registersuccess",
      component: RegisterSuccess,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: "/forgotpassword",
      name: "forgotpassword",
      component: ForgotPassword
      // this path is open to public
      // can't sign-in/authenticate yourself if you forgot your password
    },
    {
      path: "/changepassword",
      name: "changepassword",
      component: PasswordUpdate,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: "/admin/manageusers",
      name: "adminmanageusers",
      component: ManageUsers,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresAdmin: true
      }
    },
    {
      path: "/admin/manageroles",
      name: "adminmanageroles",
      component: ManageRoles,
      meta: {
        requiresAuth: true,
        requiresActivation: true,
        requiresAdmin: true
      }
    },
    {
      path: "*",
      name: "default",
      component: PageNotFound,
      meta: {
        requiresAuth: true
      }
    }
  ]
});

router.beforeEach((to, from, next) => {
  // Require login
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isLoggedIn) {
      next({ name: "login", query: { redirect: window.location.pathname } });
      return;
    }
    // Check for timestamp within 11.5 hours (token expires in 12 hours)
    if (Date.now() - store.getters.getTimestamp > 41400000) {
      next({ name: "login", query: { redirect: window.location.pathname } });
      return;
    }
  }

  // Require verification & activation
  if (to.matched.some(record => record.meta.requiresActivation)) {
    if (!store.getters.isActivated || !store.getters.isVerified) {
      next("/emptyhome");
      return;
    }
  }

  // Requires admin
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    if (!store.getters.isAdmin) {
      next("/home");
      return;
    }
  }

  // Requires any membership status except Non-member
  if (to.matched.some(record => record.meta.requiresMember)) {
    if (!store.getters.isMember && !store.getters.isSeminarOnly && !store.getters.isADSS && !store.getters.isCampOnly) {
      next("/home");
      return;
    }
  }

  // All checks passed
  next();
  return;
}
);

export default router;
